import { KpiStrip, Stat } from "@/terabizz"
import { Skeleton } from "@medusajs/ui"
import { useOrderName } from "@/lib/permissions"
import { Select, Table, Text } from "@medusajs/ui"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { analytics } from "@/analytics/api"
import { useStoreTz } from "@/analytics/pickers"
import { SettlementOverview } from "@/analytics/SettlementPanel"
import { Link, useNavigate } from "react-router-dom"
import { ErrorState, PageHeader, Section, Status, Thumb } from "@/components/ui"
import { useDashboard } from "@/lib/api"
import { formatDate, formatMoney } from "@/lib/format"
import { useCurrentMe } from "@/lib/permissions"
import type { Dashboard, Kpi } from "@/lib/types"
import { useCan } from "@/lib/permissions"
import { useIssueCounts } from "@/lib/issues"

const RANGES: { value: Dashboard["range"]; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
]
/** The same days as the Home numbers (backend dashboard DASHBOARD_PRESETS), for the settlement figures. */
const HOME_PRESETS: Record<string, string> = { today: "today", "7d": "last_7_days_incl", "30d": "last_30_days_incl", "90d": "last_90_days_incl", "365d": "last_365_days_incl" }

function KpiCard({ label, kpi, format, to, headline }: { label: string; kpi?: Kpi; format: (n: number) => string; to?: string; headline?: boolean }) {
  return kpi ? (
    <Stat strip headline={headline} label={label} value={format(kpi.value)} change={kpi.change_pct} to={to} />
  ) : (
    <div className="k-kpi" aria-busy="true">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="mt-3 h-7 w-32" />
    </div>
  )
}

/** Open problems from admin → Issues (failed payments, stuck orders, shipments, emails). */
function IssuesBanner() {
  const can = useCan()
  const { data } = useIssueCounts(can("orders:read"))
  const urgent = (data?.critical ?? 0) + (data?.high ?? 0)
  if (!urgent) return null
  const money = data?.critical ?? 0
  return (
    <Link
      to={money ? "/issues?severity=critical" : "/issues"}
      className={`mb-4 flex items-center justify-between gap-3 rounded-[var(--k-r-md)] border px-4 py-3 text-[13px] ${money ? "border-[#efc9c6] bg-[var(--tb-critical-bg)]" : "border-[#f0dca0] bg-[var(--tb-attention-bg)]"}`}
    >
      <span>
        <strong className="font-semibold">
          {urgent} problem{urgent === 1 ? "" : "s"} need{urgent === 1 ? "s" : ""} attention
        </strong>
        {money ? ` · ${money} may involve money taken without an order or a refund that didn't go through` : " · orders, shipments, subscriptions or emails that got stuck"}
      </span>
      <span className="shrink-0 text-ui-fg-interactive">Open Issues →</span>
    </Link>
  )
}

/** "● 3 live visitors" pill, like Shopify's Home: visitors active in the last 5 minutes; opens Live View. */
function LiveVisitors() {
  const q = useQuery({ queryKey: ["analytics", "live-now"], queryFn: analytics.liveNow, refetchInterval: 15_000, refetchIntervalInBackground: false, retry: false })
  if (q.isError) return null
  const n = q.data?.visitors_right_now
  return (
    <Link
      to="/analytics/live"
      className="inline-flex h-8 items-center gap-2 rounded-[var(--k-r-sm)] bg-white px-3 text-[13px] text-[var(--tb-ink)] shadow-[var(--tf-card-shadow)] transition-colors hover:bg-[var(--tb-surface-hover,#f7f7f7)]"
      title="Visitors active on your store in the last 5 minutes"
    >
      <span className="relative flex h-2 w-2" aria-hidden="true">
        {n ? <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--k-positive)] opacity-60" /> : null}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${n ? "bg-[var(--k-positive)]" : "bg-[#b5bec8]"}`} />
      </span>
      <span className="tabular-nums">{n == null ? "–" : n.toLocaleString()}</span>
      <span>live {n === 1 ? "visitor" : "visitors"}</span>
    </Link>
  )
}

export function HomePage() {
  const orderName = useOrderName()
  const me = useCurrentMe()
  const [range, setRange] = useState<Dashboard["range"]>("7d")
  const { data, error } = useDashboard(range)
  const navigate = useNavigate()
  const money = (n: number) => formatMoney(n, data?.currency_code)
  const greeting = me.user.first_name ? `Hi ${me.user.first_name}` : "Home"
  const tz = useStoreTz()

  return (
    <>
      <PageHeader
        title={greeting}
        actions={
          <div className="flex items-center gap-2">
          <LiveVisitors />
          <Select value={range} onValueChange={(v) => setRange(v as Dashboard["range"])} size="small">
            <Select.Trigger className="w-40">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              {RANGES.map((r) => (
                <Select.Item key={r.value} value={r.value}>
                  {r.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
          </div>
        }
      />
      <IssuesBanner />
      {error ? (
        <ErrorState error={error} />
      ) : (
        <div className="flex min-w-0 flex-col gap-y-4">
          <KpiStrip>
            <KpiCard headline label="Total sales" kpi={data?.kpis.total_sales} format={money} to="/analytics" />
            <KpiCard label="Orders" kpi={data?.kpis.orders} format={(n) => n.toLocaleString()} to="/orders" />
            <KpiCard label="Average order value" kpi={data?.kpis.aov} format={money} />
            <KpiCard label="New customers" kpi={data?.kpis.new_customers} format={(n) => n.toLocaleString()} to="/customers" />
          </KpiStrip>
          <SettlementOverview range={{ preset: HOME_PRESETS[range] }} tz={tz} refresh={range === "today"} className="" />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 [&>*]:min-w-0">
            <Section
              title="Recent orders"
              className="lg:col-span-2"
              actions={
                <Link to="/orders" className="txt-compact-small-plus text-ui-fg-interactive">
                  View all
                </Link>
              }
            >
              {data?.recent_orders.length === 0 ? (
                <Text size="small" className="px-4 py-8 text-ui-fg-subtle">
                  No orders yet. Orders placed on your store appear here.
                </Text>
              ) : (
                <>
                <ul className="divide-y md:hidden">
                  {data?.recent_orders.map((o) => (
                    <li key={o.id}>
                      <Link to={`/orders/${o.id}`} className="flex flex-col gap-y-1 px-4 py-3">
                        <div className="flex items-center justify-between gap-x-3">
                          <span className="k-data font-medium">{orderName(o.display_id)}</span>
                          <span className="k-data">{formatMoney(o.total, o.currency_code)}</span>
                        </div>
                        <div className="truncate text-[13px] text-ui-fg-subtle">{o.customer_name || o.email}</div>
                        <div className="flex items-center justify-between gap-x-3 text-[12px] text-ui-fg-subtle">
                          <Status value={o.status} />
                          <span className="k-data">{formatDate(o.created_at)}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Table className="hidden md:table">
                  <Table.Body>
                    {data?.recent_orders.map((o) => (
                      <Table.Row key={o.id} className="cursor-pointer" onClick={() => navigate(`/orders/${o.id}`)}>
                        <Table.Cell className="k-data font-medium">{orderName(o.display_id)}</Table.Cell>
                        <Table.Cell>{o.customer_name || o.email}</Table.Cell>
                        <Table.Cell>
                          <Status value={o.status} />
                        </Table.Cell>
                        <Table.Cell className="k-data text-ui-fg-subtle">{formatDate(o.created_at)}</Table.Cell>
                        <Table.Cell className="k-data text-right">{formatMoney(o.total, o.currency_code)}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
                </>
              )}
            </Section>

            <Section title="Low stock">
              {data?.low_stock.items.length === 0 ? (
                <Text size="small" className="px-4 py-8 text-ui-fg-subtle">
                  Everything is above {data.low_stock.threshold} units.
                </Text>
              ) : (
                <ul className="divide-y">
                  {data?.low_stock.items.map((i) => (
                    <li key={i.inventory_item_id + i.location_id} className="flex items-center justify-between gap-x-3 px-4 py-2.5">
                      <div className="flex min-w-0 items-center gap-x-3">
                        <Thumb src={(i as any).thumbnail} />
                        <div className="min-w-0">
                          <Text size="small" weight="plus" className="truncate">
                            {(i as any).product_id ? <Link to={`/products/${(i as any).product_id}`} className="hover:underline">{i.title ?? "Untitled"}</Link> : i.title ?? "Untitled"}
                          </Text>
                          <Text size="xsmall" className="truncate text-ui-fg-muted">
                            {[(i as any).variant_title, i.sku].filter(Boolean).join(" · ") || "No SKU"}
                          </Text>
                        </div>
                      </div>
                      <Text size="small" weight="plus" className={"k-data " + (i.available <= 0 ? "text-ui-tag-red-text" : "text-ui-tag-orange-text")}>
                        {i.available}
                      </Text>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        </div>
      )}
    </>
  )
}
